// src/hooks/useMasterKey.ts
// Manages Master Key lifecycle.
// Call this hook ONCE at AuthenticatedLayout root — not in individual components.
//
// SECURITY:
// - Password passed as prop, not stored in hook state.
// - MK lives in MasterKeyManager module memory only.
// - Calls MasterKeyManager.clear() on unmount (logout).
//
// NOTE: GET /keys/my-master-key always returns HTTP 200 (never 404).
//   { bootstrapped: false } → user hasn't set up ZKPA yet
//   { bootstrapped: true, ...encMK } → key material for decryption

import { useEffect } from "react";
import { MasterKeyManager } from "@/shared/lib/crypto/masterKey";
import { zkpaApi } from "@/shared/api/zkpa.api";
import { useZKPAStore } from "@/shared/stores/zkpaStore";
import type { ZKPAKeyState } from "@/shared/lib/crypto/types";

interface UseMasterKeyOptions {
  userId: string | null;
  /** Login password — used to decrypt encMK. Never stored in hook state. */
  password: string | null;
}

/**
 * useMasterKey — loads Master Key into MasterKeyManager on mount.
 * Returns current ZKPAKeyState (same as useZKPAStatus).
 *
 * Call once at AuthenticatedLayout. All other components use useZKPAStatus() only.
 */
export function useMasterKey({ userId, password }: UseMasterKeyOptions): ZKPAKeyState {
  const { keyState, setKeyState } = useZKPAStore();

  useEffect(() => {
    // Guard: browser support
    if (!MasterKeyManager.isSupported()) {
      setKeyState({ status: "browser_unsupported" });
      return;
    }

    // Guard: not authenticated
    if (!userId) {
      setKeyState({ status: "uninitialized" });
      return;
    }

    // cancelled must be declared here so all async closures below can safely reference it
    let cancelled = false;

    // Password not in memory — check server to distinguish "never set up" vs "needs unlock"
    if (!password) {
      setKeyState({ status: "loading" });
      async function checkBootstrapped() {
        try {
          const status = await zkpaApi.getEncryptedMasterKey();
          if (!cancelled) {
            // bootstrapped=true  → already set up, password just not in memory (page refresh)
            // bootstrapped=false → never bootstrapped → show setup wizard
            setKeyState(status.bootstrapped ? { status: "needs_unlock" } : { status: "uninitialized" });
          }
        } catch {
          // Only real network errors reach here (backend always returns 200)
          if (!cancelled) setKeyState({ status: "uninitialized" });
        }
      }
      checkBootstrapped();
      return () => { cancelled = true; };
    }

    // Already loaded in this session
    if (MasterKeyManager.isLoaded()) {
      const bytes = MasterKeyManager.getPublicKeyBytes();
      if (bytes) setKeyState({ status: "ready", publicKeyBytes: bytes });
      return;
    }

    async function loadKey() {
      setKeyState({ status: "loading" });
      try {
        // Fetch key status + material in one call (encMK + encSK + PBKDF2 params)
        const status = await zkpaApi.getEncryptedMasterKey();
        if (!status.bootstrapped) {
          // Not bootstrapped — parent renders SeedPhraseSetupModal
          if (!cancelled) setKeyState({ status: "uninitialized" });
          return;
        }

        // TypeScript narrowed: status is { bootstrapped: true } & EncryptedMasterKeyResponse
        // Decrypt and load into module memory — all fields come from the same response
        await MasterKeyManager.decryptAndLoad({
          encryptedMasterKey: status.encrypted_master_key,
          nonce: status.master_key_nonce,
          pbkdf2Salt: status.pbkdf2_salt,
          pbkdf2Iterations: status.pbkdf2_iterations,
          encryptedPrivateKey: status.encrypted_private_key,
          privateKeyNonce: status.private_key_nonce,
          password: password!,
          userId: userId!,
        });

        if (!cancelled) {
          const bytes = MasterKeyManager.getPublicKeyBytes()!;
          setKeyState({ status: "ready", publicKeyBytes: bytes });
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Không thể giải mã khóa";
          setKeyState({ status: "needs_unlock", error: message });
        }
      }
    }

    loadKey();

    return () => {
      cancelled = true;
    };
  }, [userId, password, setKeyState]);

  // Clear all keys from memory when component unmounts (i.e., user logged out)
  useEffect(() => {
    return () => {
      MasterKeyManager.clear();
      setKeyState({ status: "uninitialized" });
    };
  }, [setKeyState]);

  return keyState;
}
